import {
  type DocumentReference,
  getDocs,
  query,
  Timestamp,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { decksCollection } from '../../../firebase/collections';
import { riftboundToaster } from '../../../components/ui/toaster';
import { clearTeamFeaturedDeckIfMatches } from '../../../utils/featuredDeck';

async function commitInChunks(
  docs: Array<{ ref: DocumentReference; update: Record<string, unknown> }>
): Promise<void> {
  for (let i = 0; i < docs.length; i += 400) {
    const batch = writeBatch(db);
    for (const d of docs.slice(i, i + 400)) {
      batch.update(d.ref, d.update);
    }
    await batch.commit();
  }
}

/**
 * Downgrade the signed-in user's team-only decks to private, and detach
 * public decks from the former team (keep them public, clear teamId).
 * Called after leave / kick / team delete when users.teamId becomes null.
 */
export async function downgradeOwnTeamDecks(
  uid: string,
  formerTeamId?: string | null
): Promise<number> {
  const now = Timestamp.now();
  const teamSnap = await getDocs(
    query(
      decksCollection(),
      where('ownerId', '==', uid),
      where('visibility', '==', 'team')
    )
  );
  const publicSnap = await getDocs(
    query(
      decksCollection(),
      where('ownerId', '==', uid),
      where('visibility', '==', 'public')
    )
  );

  const teamUpdates = teamSnap.docs.map((d) => ({
    ref: d.ref,
    update: { visibility: 'private' as const, teamId: null, updatedAt: now },
  }));
  const publicUpdates = publicSnap.docs
    .filter((d) => typeof d.data().teamId === 'string' && d.data().teamId)
    .map((d) => ({
      ref: d.ref,
      update: { teamId: null, updatedAt: now },
    }));

  await commitInChunks([...teamUpdates, ...publicUpdates]);

  // Team-only decks become private — drop them from the former team's featured slot.
  if (formerTeamId) {
    await Promise.all(
      teamSnap.docs.map((d) => clearTeamFeaturedDeckIfMatches(formerTeamId, d.id))
    );
  }

  return teamUpdates.length;
}

/**
 * Stamp teamId onto the user's public decks so they appear on the team profile.
 * Safe to call repeatedly; skips decks already linked to this team.
 */
export async function associatePublicDecksWithTeam(
  uid: string,
  teamId: string
): Promise<number> {
  const snap = await getDocs(
    query(
      decksCollection(),
      where('ownerId', '==', uid),
      where('visibility', '==', 'public')
    )
  );
  if (snap.empty) return 0;

  const now = Timestamp.now();
  const needUpdate = snap.docs.filter((d) => d.data().teamId !== teamId);
  if (needUpdate.length === 0) return 0;

  await commitInChunks(
    needUpdate.map((d) => ({
      ref: d.ref,
      update: { teamId, updatedAt: now },
    }))
  );
  return needUpdate.length;
}

/** React helper: run cleanup once when teamId transitions to null. */
export function notifyTeamDecksPrivatized(count: number): void {
  if (count <= 0) return;
  riftboundToaster.create({
    title: 'Your team-only decks are now private',
    description:
      count === 1
        ? '1 deck is visible only to you. Re-share with a team anytime.'
        : `${count} decks are visible only to you. Re-share with a team anytime.`,
    type: 'info',
  });
}
